import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { joiResolver } from '@hookform/resolvers/joi';
import Joi from 'joi';
import clsx from 'clsx';
import { Navigate, useNavigate } from 'react-router-dom';
import { Icon } from '../../layout/Icon.jsx';
import { toast } from '../../shared/useToast.js';
import { PortalFrame } from './components/PortalFrame.jsx';
import { usePlatformAuth } from './usePlatformAuth.js';
import { platformError } from './platformApi.js';

const schema = Joi.object({
  username: Joi.string().trim().min(2).required().messages({
    'string.empty': 'اسم المستخدم مطلوب',
    'string.min': 'اسم المستخدم قصير جداً',
    'any.required': 'اسم المستخدم مطلوب'
  }),
  password: Joi.string().min(6).required().messages({
    'string.empty': 'كلمة المرور مطلوبة',
    'string.min': 'كلمة المرور يجب أن تكون 6 أحرف على الأقل',
    'any.required': 'كلمة المرور مطلوبة'
  })
});

export function PlatformLoginScreen() {
  const navigate = useNavigate();
  const user = usePlatformAuth((s) => s.user);
  const ready = usePlatformAuth((s) => s.ready);
  const login = usePlatformAuth((s) => s.login);

  const [serverError, setServerError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm({
    resolver: joiResolver(schema),
    defaultValues: { username: '', password: '' }
  });

  if (ready && user) {
    return <Navigate to={user.mustChangePassword ? '/platform/change-password' : '/platform'} replace />;
  }

  const onSubmit = async (values) => {
    setServerError('');
    setLoading(true);
    try {
      const logged = await login({ username: values.username.trim(), password: values.password.trim() });
      toast.success('مرحباً بك في لوحة مدير النظام');
      navigate(logged?.mustChangePassword ? '/platform/change-password' : '/platform', { replace: true });
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401) setServerError('اسم المستخدم أو كلمة المرور غير صحيحة');
      else if (status === 429) setServerError('محاولات كثيرة جداً — انتظر قليلاً ثم أعد المحاولة');
      else setServerError(platformError(err, 'تعذّر تسجيل الدخول'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <PortalFrame title="لوحة مدير النظام" sub="إدارة شركات منصّة Kids Zone">
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <div>
          <label className="block text-[12px] font-semibold text-ink-2 mb-1.5">اسم المستخدم</label>
          <div className="relative">
            <span className="absolute top-1/2 -translate-y-1/2 right-3 text-ink-3">
              <Icon name="users" size={16} />
            </span>
            <input
              type="text"
              autoComplete="username"
              autoFocus
              dir="ltr"
              className={clsx('input pr-9', errors.username && 'border-red')}
              style={{ textAlign: 'left' }}
              placeholder="superadmin"
              {...register('username')}
            />
          </div>
          {errors.username ? (
            <div className="text-red text-[11.5px] mt-1 font-semibold">{errors.username.message}</div>
          ) : null}
        </div>

        <div className="mt-3">
          <label className="block text-[12px] font-semibold text-ink-2 mb-1.5">كلمة المرور</label>
          <div className="relative">
            <span className="absolute top-1/2 -translate-y-1/2 right-3 text-ink-3">
              <Icon name="settings" size={16} />
            </span>
            <input
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              dir="ltr"
              className={clsx('input pr-9 pl-10', errors.password && 'border-red')}
              style={{ textAlign: 'left' }}
              placeholder="••••••••"
              {...register('password')}
            />
            <button
              type="button"
              className="absolute top-1/2 -translate-y-1/2 left-2 text-ink-3 hover:text-ink p-1"
              onClick={() => setShowPassword((v) => !v)}
              tabIndex={-1}
              title={showPassword ? 'إخفاء' : 'عرض'}
            >
              {showPassword ? <Icon name="x" size={14} /> : <Icon name="check" size={14} />}
            </button>
          </div>
          {errors.password ? (
            <div className="text-red text-[11.5px] mt-1 font-semibold">{errors.password.message}</div>
          ) : null}
        </div>

        {serverError ? (
          <div className="mt-3 px-3 py-2.5 rounded-[10px] bg-[#FEE2E2] border border-[#FCA5A5] text-[#B91C1C] text-[12.5px] font-semibold flex items-center gap-2">
            <Icon name="x" size={14} />
            {serverError}
          </div>
        ) : null}

        <button
          type="submit"
          disabled={loading}
          className={clsx('btn btn-primary btn-xl w-full justify-center mt-4', loading && 'opacity-70 cursor-not-allowed')}
        >
          {loading ? 'جاري الدخول...' : (
            <>
              دخول
              <Icon name="chevronL" size={16} />
            </>
          )}
        </button>

        <div className="text-center mt-5 text-[11px] text-ink-3">
          هذه البوابة لمدير المنصّة فقط — موظفو الشركات يسجّلون الدخول من الشاشة الرئيسية.
        </div>
      </form>
    </PortalFrame>
  );
}
